import React, {useEffect} from 'react';
import clsx from 'clsx';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import MuiDialogContent from '@material-ui/core/DialogContent';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import {createStyles, makeStyles, Theme} from "@material-ui/core/styles";
import projections, {defaultProjection, defaultTransform, generateID} from './projections';
import EnhancedTable from '../enhanced-table';
import {useState} from 'react';
import proj4 from "proj4";
import {
  AppBar,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@material-ui/core';
import LocalScene from '../viewer/js/projections';
import { PanelProps, TabPanel, TabProps } from '../tabs';
import { useSnackbar } from 'notistack';
import { ipcRenderer } from '../electron-modules';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    title: {
      margin: 0,
      padding: theme.spacing(1,2,1,2),
    },
    content: {
      padding: theme.spacing(0),
      paddingTop: "0px !important"
    },
    titleClose: {
      position: 'absolute',
      right: theme.spacing(0),
      color: theme.palette.grey[500],
    },
    userWarning: {
      padding: theme.spacing(3)
    },
    marginTop: {
      marginTop: theme.spacing(2)
    },
    marginBottom: {
      marginBottom: theme.spacing(2)
    },
    adornedEnd: {
      paddingRight: theme.spacing(0)
    },
    utmButton: {
      marginLeft: theme.spacing(1)
    },
    selectMargin: {
      margin: theme.spacing(1),
    },
    button: {
      marginTop: theme.spacing(2),
      marginLeft: theme.spacing(1)
    },
    dropdown: {
      minWidth: 200,
    },
    formControl: {
      width: '100%'
    },
    buttonDiv: {
      textAlign: "right"
    },
    panel: {
      background: "rgba(200,200,200,0.1)"
    }
  }),
);

const UserProjectionPanel = (props) => {
  const {classes, loadProjectionToEdit, setActiveTab, showMessage} = props;

  // Table headers
  const columns = [
    {id: 'name', numeric: false, label: 'Projection Name'},
    {id: 'type', numeric: false, label: 'Type'},
    {id: 'custom', numeric: false, label: 'Custom'},
  ];

  // Convert projections to data row
  const data = projections.data;
  const rows = data.map(option => {
    return {
      id: generateID(option.name),
      name: option.name,
      custom: option.transform ? "Yes" : "No",
      type: option.type,
      disabled: LocalScene.currentProjection(option.name)
    };
  });

  const onRowClick = (row) => {
    const results = data.filter(x => x.name === row.name);
    if (results.length !== 1) {
      return;
    }

    const projection = results[0];
    loadProjectionToEdit(projection, false);
    setActiveTab(1);
  };

  const onDeleteClick = (row) => {
    const results = data.filter(x => x.name === row.name);
    if (results.length !== 1) {
      return;
    }

    // Delete projection + force reload
    const projection = results[0];
    projections.delete(projection.name);
    showMessage("Projection deleted", {
      variant: "error"
    });
  };

  return (
    <div className={classes.user}>
      {rows.length === 0 && (<Typography variant="subtitle1" className={classes.userWarning}>
        No user projections have been added. Click on "Add New" to add a custom
        projection or "Search Online" to look up common results
      </Typography>)}

      {rows.length > 0 && (<EnhancedTable
        columns={columns}
        rows={rows}
        rowType="projection"
        onRowClick={onRowClick}
        deletable={true}
        onDeleteClick={onDeleteClick}
        dense={true}
      />)}
    </div>
  );
};

const EditPanel = (props) => {
  const {classes, customProjection, updateEditableValues,
    transformCheckbox, setTransformCheckbox, clearProjectionValues,
    newProjection, setActiveTab, existingName, showMessage, errors,
    setErrors} = props;

  const name = customProjection.name;
  const type = customProjection.type;
  const projString = customProjection.string;

  const transform = customProjection.transform;
  const origin = transform.origin;
  const translate = transform.translation;
  const rotation = transform.rotation;
  const scale = transform.scale;

  const handleChecked = (event) => {
    setTransformCheckbox(event.target.checked);
  };

  const saveProjection = () => {
    // Remove excess whitespace from name and string
    let nameTrim = name.trim();
    let stringTrim = projString.trim();

    let nameError = nameTrim === "";
    let stringError = stringTrim === "";
    setErrors({
      name: nameError,
      string: stringError
    });

    if (nameError || stringError) {
      return;
    }

    // Create copy of our data
    let data = JSON.parse(JSON.stringify(customProjection));

    // Use trimmed versions of name and string
    data.name = nameTrim;
    data.string = stringTrim;

    if (!transformCheckbox) {
      delete data.transform;
    }

    const nameChanged = data.name !== existingName;

    // Check for duplicates if our name has changed
    if (nameChanged) {
      if (projections.isDuplicate(data)) {
        setErrors({...errors, name: true});
        showMessage("Duplicate projection (name already exists)", {
          variant: "error"
        });
        return;
      }
    }

    // Check for valid projection string
    try {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      let projection = proj4(projString);
    } catch {
      setErrors({...errors, string: true});
      showMessage("Invalid projection string", {
        variant: "error"
      });
      return;
    }

    // Delete existing projection
    if (!newProjection) {
      projections.delete(existingName);
    }

    // Save new projection
    const success = projections.save(data);
    if (!success) {
      return;
    }

    showMessage(`Projection ${newProjection ? "added" : "updated"}`, {
      variant: "success"
    });

    if (newProjection) {
      clearProjectionValues();
    } else {
      setActiveTab(0);
    }
  };

  const updateType = (event) => {
    // Update projection type and clear projection string
    updateEditableValues({
      'type': String(event.target.value),
      'string': ""
    });
  };

  const updateName = (event) => {
    setErrors({...errors, name: false});
    updateEditableValues({
      "name": event.target.value
    });
  };

  const updateString = (event) => {
    let string = event.target.value;
    if (customProjection['type'] === "epsg") {
      string = `EPSG:${string}`;
    }
    setErrors({...errors, string: false});
    updateEditableValues({
      "string": string
    });
  };

  const updateOrigin = (event, index) => {
    const value = parseFloat(event.target.value);
    let transform = customProjection.transform;
    transform['origin'][index] = value;
    updateEditableValues({
      "transform":transform
    });
  };

  const updateTranslation = (event, index) => {
    const value = parseFloat(event.target.value);
    let transform = customProjection.transform;
    transform['translation'][index] = value;
    updateEditableValues({
      "transform":transform
    });
  };

  const updateScale = (event) => {
    let transform = customProjection.transform;
    transform['scale'] = parseFloat(event.target.value);
    updateEditableValues({
      "transform":transform
    });
  };

  const updateRotation = (event) => {
    let transform = customProjection.transform;
    transform['rotation'] = parseFloat(event.target.value);
    updateEditableValues({
      "transform":transform
    });
  };

  return (
    <div>
      {/* Projection Name */}
      <TextField
        label="Projection Name"
        fullWidth
        placeholder="Custom Projection"
        margin="dense"
        InputLabelProps={{
          shrink: true,
        }}
        value={name}
        variant="outlined"
        error={errors.name}
        onChange={updateName}
        aria-describedby="name-error-text"
      />

      {/* Projection Type */}
      <div className={classes.selectMargin}>
        <InputLabel id="proj-type-select-label">
          Projection Type
        </InputLabel>
        <Select
          labelId="proj-type-select-label"
          id="proj-type-select"
          value={type}
          onChange={updateType}
          className={classes.dropdown}
        >
          <MenuItem value={"proj4"}>Projection String (Proj4)</MenuItem>
          <MenuItem value={"wkt"}>Well Known Text (WKT)</MenuItem>
          {/*<MenuItem value={"epsg"}>EPSG Code</MenuItem>*/}
        </Select>
      </div>

      {/* Projection string (proj4) */}
      {type==="proj4" && (<TextField
        label="Proj4 Definition"
        placeholder="+proj=utm +zone=10 +ellps=WGS84 +datum=WGS84 +units=m"
        fullWidth
        multiline
        margin="dense"
        InputLabelProps={{
          shrink: true,
        }}
        variant="outlined"
        value={projString}
        onChange={updateString}
        error={errors.string}
        className={classes.marginTop}
      />)}

      {/* Projection string (WKT) */}
      {type==="wkt" && (<TextField
        label="WKT Definition"
        placeholder='PROJCS["NAD83(CSRS) / UTM zone 10N",GEOGCS["NAD83(CSRS)",DATUM["D_North_American_1983_CSRS98",SPHEROID["GRS_1980",6378137,298.257222101]],PRIMEM["Greenwich",0],UNIT["Degree",0.017453292519943295]],PROJECTION["Transverse_Mercator"],PARAMETER["latitude_of_origin",0],PARAMETER["central_meridian",-123],PARAMETER["scale_factor",0.9996],PARAMETER["false_easting",500000],PARAMETER["false_northing",0],UNIT["Meter",1]]'
        fullWidth
        multiline
        margin="dense"
        InputLabelProps={{
          shrink: true,
        }}
        variant="outlined"
        value={projString}
        onChange={updateString}
        error={errors.string}
        className={classes.marginTop}
      />)}

      {/* EPSG Code */}
      {type==="epsg" && (<TextField
        label="EPSG Number (Limited Support)"
        placeholder='3857'
        fullWidth
        type="number"
        margin="dense"
        InputLabelProps={{
          shrink: true,
        }}
        variant="outlined"
        value={projString.replace("EPSG:","")}
        onChange={updateString}
        error={errors.string}
        InputProps={{
          startAdornment: <InputAdornment position="start">EPSG:</InputAdornment>,
        }}
        className={classes.marginTop}
      />)}

      <FormControlLabel
        className={classes.marginTop}
        control={
          <Checkbox
            checked={transformCheckbox}
            onChange={handleChecked}
          />
        }
        label="Additional Parameters"
      />

      {transformCheckbox && (<Grid container >
        {/* Origin */}
        <Grid container spacing={3}>
          <Grid item xs={4}>
            <TextField
              label="Origin (X)"
              type="number"
              value={origin[0]}
              onChange={(e) => {
                updateOrigin(e, 0);
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="Origin (Y)"
              type="number"
              value={origin[1]}
              onChange={(e) => {
                updateOrigin(e, 1);
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="Origin (Z)"
              type="number"
              value={origin[2]}
              onChange={(e) => {
                updateOrigin(e, 2);
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
        </Grid>

        {/* Translation */}
        <Grid className={classes.marginTop} container spacing={3}>
          <Grid item xs={4}>
            <TextField
              label="Translation (X)"
              type="number"
              value={translate[0]}
              onChange={(e) => {
                updateTranslation(e, 0);
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="Translation (Y)"
              type="number"
              value={translate[1]}
              onChange={(e) => {
                updateTranslation(e, 1);
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="Translation (Z)"
              type="number"
              value={translate[2]}
              onChange={(e) => {
                updateTranslation(e, 2);
              }}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
        </Grid>

        {/* Rotation and scaling */}
        <Grid className={classes.marginTop} container spacing={3}>
          <Grid item xs={6}>
            <TextField
              label="Horizontal Scale"
              fullWidth
              type="number"
              value={scale}
              onChange={updateScale}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              label="Rotation"
              fullWidth
              type="number"
              value={rotation}
              onChange={updateRotation}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
        </Grid>
      </Grid>)}

      <div className={classes.buttonDiv}>
        {!newProjection && (<Button
          color="primary"
          type="submit"
          onClick={() => {
            setActiveTab(0);
          }}
          className={classes.button}
        >
          Cancel
        </Button>)}

        <Button
          color="primary"
          type="submit"
          onClick={saveProjection}
          className={classes.button}
        >
          {newProjection ? "Add Projection" : "Save Changes"}
        </Button>
      </div>
    </div>
  );
};

const SearchPanel = (props) => {
  const {classes, setActiveTab, loadProjectionToEdit} = props;

  const [type, setType] = useState("text");
  const [searchTerm, setSearchTerm] = useState("");
  const [hemisphere, setHemisphere] = useState("N");
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);

  const handleChange = (event) => {
    setType(event.target.value);
  };

  const handleHemisphere = (event) => {
    setHemisphere(event.target.value);
  };

  const handleString = (event) => {
    setSearchTerm(event.target.value);
  };

  const searchOnline = () => {
    if (searchTerm === "") {
      return;
    }

    let value = searchTerm;
    if (type === "utm") {
      value = `utm ${value}${hemisphere}`;
    }


    getProjections(value);
  };

  const getProjections = (searchText, pageNumber = 1, previousResults = [], maxPageNumber = 10) => {
    if (pageNumber === 1) {
      setSearchResults([]);
      setSearching(true);
    }

    const text = type !== "epsg" ?
      `${searchText} kind:PROJCRS` :
      searchText;

    const url = "https://epsg.io/?q=" + text + "&format=json&page=" + pageNumber;

    fetch(url)
      .then(response => response.json())
      .then(data => {
        const results = data.results;
        const parsed = [];

        results.forEach(result => {
          if (type === "epsg") {
            if (result.code !== searchText) {
              return;
            }
          }

          if (result.proj4 === "") {
            return;
          }

          parsed.push(result);
        });

        const combined = [...previousResults, ...parsed];
        setSearchResults(combined);

        if ((results.length > 0) && (pageNumber < maxPageNumber)) {
          setTimeout(() =>{
            getProjections(searchText, pageNumber + 1, combined);
          }, 250);
        } else {
          setSearching(false);
        }
      });
  };

  const onRowClick = (row) => {
    loadProjectionToEdit({
      type: "proj4",
      name: row.name,
      string: row.proj4
    }, true);
    setActiveTab(1);
  };

  // Table headers
  const columns = [
    {id: 'name', numeric: false, label: 'Name'},
    {id: 'epsg', numeric: true, label: 'EPSG'},
    {id: 'proj4', numeric: false, label: 'Proj4 String'},
  ];

  // Convert projections to data row
  const data = searchResults;
  const rows = data.map(projection => {
    return {
      id: generateID(projection.name),
      name: projection.name,
      epsg: parseInt(projection.code),
      proj4: projection.proj4,
    };
  });

  return (
    <div>
      {/* Search Type */}
      <div className={classes.selectMargin}>
        <InputLabel id="proj-type-select-label">
          Search Type
        </InputLabel>
        <Select
          labelId="proj-type-select-label"
          id="search-type-select"
          value={type}
          onChange={handleChange}
          className={classes.dropdown}
        >
          <MenuItem value={"text"}>Text</MenuItem>
          <MenuItem value={"utm"}>UTM Zone</MenuItem>
          <MenuItem value={"epsg"}>EPSG Code</MenuItem>
        </Select>
      </div>

      {/* UTM Zone */}
      {type==="utm" && (<div>
        <TextField
          label="UTM Zone"
          placeholder='10'
          type="number"
          margin="dense"
          InputLabelProps={{
            shrink: true,
          }}
          variant="outlined"
          onChange={handleString}
          InputProps={{
            startAdornment: <InputAdornment position="start">UTM Zone</InputAdornment>,

            classes: {
              adornedEnd: classes.adornedEnd
            }
          }}
          className={classes.marginTop}
        />

        <TextField
          select
          label="Hemisphere"
          margin="dense"
          value={hemisphere}
          variant="outlined"
          onChange={handleHemisphere}
          className={clsx(classes.marginTop, classes.utmButton)}
        >
          <MenuItem key={"N"} value="N">North</MenuItem>
          <MenuItem key={"S"} value="S">South</MenuItem>
        </TextField>

      </div>)}

      {/* EPSG Code */}
      {type==="epsg" && (<TextField
        label="EPSG Number"
        placeholder='3857'
        type="number"
        margin="dense"
        InputLabelProps={{
          shrink: true,
        }}
        variant="outlined"
        onChange={handleString}
        InputProps={{
          startAdornment: <InputAdornment position="start">EPSG:</InputAdornment>,
        }}
        className={classes.marginTop}
      />)}

      {/* Projection string (proj4) */}
      {type==="text" && (<TextField
        label="Text Search"
        placeholder="texas north"
        fullWidth
        multiline
        margin="dense"
        InputLabelProps={{
          shrink: true,
        }}
        variant="outlined"
        onChange={handleString}
        className={classes.marginTop}
      />)}

      {/* Search button */}
      <div className={classes.buttonDiv}>
        <Button
          color="primary"
          type="submit"
          disabled={searching}
          onClick={searchOnline}
          className={classes.button}
        >
          {searching ? "Searching EPSG.io..." : "Search Online"}
        </Button>
      </div>

      {/* Results table */}
      <div
        hidden={rows.length === 0}
      >
        <Divider className={classes.marginTop}/>
        <EnhancedTable
          columns={columns}
          rows={rows}
          deletable={false}
          onRowClick={onRowClick}
          className={classes.marginTop}
          dense={true}
        />
      </div>
    </div>
  );
};

export const ProjectionsManager = () => {
  const classes = useStyles();
  const showMessage = useSnackbar().enqueueSnackbar;

  const [open, setOpen] = useState(false);
  const [existingName, setExistingName] = useState("");
  const [newProjection, setNewProjection] = useState(false);
  const [activeTab, setTabValue] = useState(0);
  const [customProjection, setCustomProjection] = useState({});
  const [transformCheckbox, setTransformCheckbox] = useState(false);
  const [errors, setErrors] = useState({});

  const setActiveTab = (value) => {
    if (value === 0) {
      clearProjectionValues();
    }

    setTabValue(value);
  };

  const clearAllErrors = () => {
    setErrors({
      name: false,
      string: false
    });
  };

  const updateEditableValues = (newValues) => {
    const combined = {...customProjection, ...newValues};
    setCustomProjection(combined);
  };

  const clearProjectionValues = () => {
    loadProjectionToEdit(defaultProjection, true);
  };

  const loadProjectionToEdit = (projectionData, newProjection) => {
    const dataCopy = JSON.parse(JSON.stringify(projectionData));
    const hasTransform = dataCopy.hasOwnProperty("transform");
    if (!hasTransform) {
      const transformCopy = JSON.parse(JSON.stringify(defaultTransform));
      dataCopy.transform = transformCopy;
    }

    clearAllErrors();
    setTransformCheckbox(hasTransform);
    setCustomProjection({...dataCopy});
    setExistingName(dataCopy.name);
    setNewProjection(newProjection);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleChange = (event, newactiveTab) => {
    setActiveTab(newactiveTab);
  };

  useEffect(() => {
    ipcRenderer?.on("open-projection-manager", () => {
      handleOpen();
    });
  }, []);

  useEffect(() => {
    // Load default projection for debug purposes
    clearProjectionValues();
    clearAllErrors();
  }, []);

  const tabLabel = newProjection ? "Add New" : "Edit Existing";

  return (
    <div>
      <Dialog
        onClose={handleClose}
        open={open}
        fullWidth={true}
        scroll="body"
      >
        <MuiDialogContent dividers className={classes.content}>
          <AppBar position="static" color="default">
            <Tabs
              value={activeTab}
              onChange={handleChange}
              indicatorColor="primary"
              textColor="primary"
            >
              <Tab label="User Projections" {...TabProps(0)} />
              <Tab label={tabLabel} {...TabProps(1)} />
              <Tab label="Search Online" {...TabProps(2)} />
            </Tabs>
            <IconButton
              aria-label="close"
              className={classes.titleClose}
              onClick={handleClose}
            >
              <CloseIcon />
            </IconButton>
          </AppBar>

          {/* Existing projections */}
          <TabPanel classes={classes} {...PanelProps(activeTab, 0)}>
            <UserProjectionPanel
              classes={classes}
              loadProjectionToEdit={loadProjectionToEdit}
              setActiveTab={setActiveTab}
              showMessage={showMessage}
            />
          </TabPanel>

          {/* Custom projections */}
          <TabPanel classes={classes} {...PanelProps(activeTab, 1)}>
            <EditPanel
              classes={classes}
              customProjection={customProjection}
              updateEditableValues={updateEditableValues}
              transformCheckbox={transformCheckbox}
              setTransformCheckbox={setTransformCheckbox}
              clearProjectionValues={clearProjectionValues}
              newProjection={newProjection}
              existingName={existingName}
              setActiveTab={setActiveTab}
              showMessage={showMessage}
              setErrors={setErrors}
              errors={errors}
            />
          </TabPanel>

          {/* Online projection search */}
          <TabPanel classes={classes} {...PanelProps(activeTab, 2)}>
            <SearchPanel
              classes={classes}
              setActiveTab={setActiveTab}
              loadProjectionToEdit={loadProjectionToEdit}
            />
          </TabPanel>

        </MuiDialogContent>
      </Dialog>
    </div>
  );
};
